import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SourceBadge } from './SourceBadge';
import { KaneaIcon } from './KaneaIcon';
import { DataGrid } from './DataGrid';
import { ChatChart } from './ChatChart';
import { ChatTable } from './ChatTable';
import { decoupeReponse, texteSeul } from './blocs';
import { ChatMessage } from '../../types/chat';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowRight, Check, Copy, Pencil } from 'lucide-react';

interface ChatMessagesProps {
  messages: ChatMessage[];
  onReformuler: (question: string) => void;
}

const heure = (d: Date) => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

const SEUIL_BAS_PX = 120;
const MARGE_HAUT_PX = 16;

const Reponse: React.FC<{ contenu: string }> = React.memo(({ contenu }) => {
  const { segments } = useMemo(() => decoupeReponse(contenu), [contenu]);
  return (
    <>
      {segments.map((s, i) => {
        if (s.type === 'graphique') return <ChatChart key={i} bloc={s.bloc} />;
        if (s.type === 'tableau') return <ChatTable key={i} bloc={s.bloc} />;
        if (s.type === 'grille') return <DataGrid key={i} items={s.items} />;
        return <ReactMarkdown key={i} remarkPlugins={[remarkGfm]}>{s.texte}</ReactMarkdown>;
      })}
    </>
  );
});

const BoutonCopier: React.FC<{ contenu: string }> = ({ contenu }) => {
  const [copie, setCopie] = useState(false);
  const copier = async () => {
    try {
      await navigator.clipboard.writeText(texteSeul(contenu));
      setCopie(true);
      window.setTimeout(() => setCopie(false), 2000);
    } catch (e) {
      console.error('Copie impossible', e);
    }
  };
  return (
    <button type="button" className="message-action" onClick={copier} aria-label="Copier la réponse">
      {copie ? <Check size={13} /> : <Copy size={13} />}
      <span>{copie ? 'Copié' : 'Copier'}</span>
    </button>
  );
};

export const ChatMessages: React.FC<ChatMessagesProps> = ({ messages, onReformuler }) => {
  const idDernier = messages.length ? messages[messages.length - 1].id : null;
  const conteneurRef = useRef<HTMLDivElement>(null);
  const dernierRef = useRef<HTMLDivElement>(null);
  const [loinDuBas, setLoinDuBas] = useState(false);

  // À chaque nouveau message, on amène le DÉBUT du dernier message à l'écran : une
  // longue réponse se lit depuis sa première ligne.
  // On fait défiler le CONTENEUR, jamais `scrollIntoView` : celui-ci déplace aussi les
  // ancêtres et faisait sortir l'en-tête de la page (constaté à l'écran le 2026-10-01).
  useEffect(() => {
    const el = conteneurRef.current;
    const dernier = dernierRef.current;
    if (!el || !dernier) return;
    const haut = dernier.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - MARGE_HAUT_PX;
    el.scrollTo({ top: Math.max(0, haut), behavior: 'smooth' });
    // Rejoué quand la réponse remplace l'indicateur d'attente (même nombre de messages,
    // autre identifiant) : sinon la réponse arrivait sous le pli.
  }, [messages.length, idDernier]);

  const surDefilement = useCallback(() => {
    const el = conteneurRef.current;
    if (el) setLoinDuBas(el.scrollHeight - el.scrollTop - el.clientHeight > SEUIL_BAS_PX);
  }, []);

  useEffect(surDefilement, [messages, surDefilement]);

  const allerEnBas = () => {
    const el = conteneurRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  const indexDernier = messages.length - 1;

  return (
    <div className="messages-zone">
      <div className="messages-container" role="log" aria-live="polite" ref={conteneurRef} onScroll={surDefilement}>
        {messages.map((message, index) => {
          const estDernier = index === indexDernier;
          if (message.role === 'user') {
            return (
              <div key={message.id} className="message-row message-user" ref={estDernier ? dernierRef : null}>
                <div className="message-body">
                  <div className="bubble">{message.content}</div>
                  <div className="message-meta message-meta--user">
                    <button type="button" className="message-action" onClick={() => onReformuler(message.content)}
                            aria-label="Reformuler cette question">
                      <Pencil size={12} />
                      <span>Reformuler</span>
                    </button>
                    <span className="timestamp">{heure(message.timestamp)}</span>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div key={message.id} className="message-row message-assistant" ref={estDernier ? dernierRef : null}>
              <div className="bot-avatar">
                <KaneaIcon size={14} color="#22c55e" />
              </div>
              <div className="message-body">
                {message.isLoading ? (
                  <div className="attente" role="status">
                    <div className="typing-indicator">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                    <span>Nia consulte les données de marché…</span>
                  </div>
                ) : (
                  <>
                    <div className="markdown-content">
                      <Reponse contenu={message.content} />
                      {message.showUpgrade && (
                        <div className="upgrade-encart">
                          <Link to="/upgrade" className="upgrade-lien">
                            Passer au plan PRO <ArrowRight size={16} />
                          </Link>
                        </div>
                      )}
                    </div>
                    <div className="message-meta">
                      <BoutonCopier contenu={message.content} />
                      {message.dataDate && (
                        <span className="source-badge source-badge--seance" title="Date de la dernière séance de cotation analysée">
                          Séance du {message.dataDate}
                        </span>
                      )}
                      {message.sources && message.sources.map((source, sIdx) => (
                        <SourceBadge key={`${message.id}-src-${sIdx}`} source={source} />
                      ))}
                      <span className="timestamp">{heure(message.timestamp)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {loinDuBas && (
        <button type="button" className="retour-bas" onClick={allerEnBas} aria-label="Revenir au dernier message">
          <ArrowDown size={16} />
        </button>
      )}
    </div>
  );
};
